import React, { useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  dashboardMenu,
  applicationsMenu,
  pagesMenu,
  uiElementsMenu,
  labMenu,
  apps,
  myDocumentsMenu,
} from "../data/Menu";
import useProfileStore from "../store/patient/profileStore";
import logo from "../assets/dedaImg/dedalogo.png";
import ChatGlobal from "../apps/GlobalChat";
import avatar from "../assets/img/user-avatar.png";
import { parseAvatar } from "../utils/chat/utils";

const menuSections = [
  { label: "My Health", menu: dashboardMenu, useHashAwareLinks: true },
  { label: "Appointments", menu: applicationsMenu, useHashAwareLinks: true },
  { label: "Medications", menu: pagesMenu, useHashAwareLinks: true },
  { label: "Examinations", menu: labMenu, useHashAwareLinks: true },
  { label: "Billing & Insurance", menu: uiElementsMenu, useHashAwareLinks: true },
  { label: "Documents", menu: myDocumentsMenu, useHashAwareLinks: false },
  { label: "Telemedicine", menu: apps, useHashAwareLinks: false },
];

const Sidebar = () => {
  const scrollBarRef = useRef(null);
  const { profile, saveprofile } = useProfileStore();

  const toggleFooterMenu = (event) => {
    event.preventDefault();
    const parent = event.currentTarget.closest(".sidebar");
    parent.classList.toggle("footer-menu-show");
  };

  const handleLogout = () => {
    saveprofile(null);
    localStorage.setItem("user", null);
    localStorage.setItem("token", null);
    window.location.href = "/pages/signin2";
  };

  const fullName =
    `${profile?.other_names || ""} ${profile?.surname || ""}`.trim() || "Patient";

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <Link to="/dashboard/overview" className="sidebar-brand">
          <span className="sidebar-brand-mark">
            <img src={logo} alt="Deda Hospital" />
          </span>
          <span className="sidebar-brand-copy">
            <strong>Deda Hospital</strong>
            <small>Secure patient portal</small>
          </span>
        </Link>
      </div>

      <PerfectScrollbar
        className="sidebar-body"
        containerRef={(ref) => {
          scrollBarRef.current = ref;
        }}
      >
        <SidebarMenu onUpdateSize={() => {}} />
      </PerfectScrollbar>

      <div className="sidebar-footer">
        <div className="sidebar-footer-top">
          <div className="sidebar-footer-thumb">
            <img
              src={profile?.profile_pic ? parseAvatar(profile.profile_pic) : avatar}
              alt={fullName}
            />
          </div>
          <div className="sidebar-footer-body">
            <h6>
              <Link to="/pages/profile">{fullName}</Link>
            </h6>
            <p>Patient</p>
          </div>
          <Link onClick={toggleFooterMenu} to="" className="dropdown-link">
            <i className="ri-arrow-down-s-line"></i>
          </Link>
        </div>

        <div className="sidebar-footer-menu">
          <nav className="nav">
            <Link to="/pages/profile">
              <i className="ri-profile-line"></i>
              View Profile
            </Link>
          </nav>

          <hr />

          <nav className="nav">
            <Link to="" onClick={handleLogout}>
              <i className="ri-logout-box-r-line"></i>
              Log Out
            </Link>
          </nav>
        </div>
      </div>
    </div>
  );
};

const SidebarMenu = ({ onUpdateSize }) => {
  const location = useLocation();

  const toggleMenu = (event) => {
    event.preventDefault();
    const parent = event.target.closest(".nav-group");
    parent.classList.toggle("show");
    onUpdateSize();
  };

  const toggleSubMenu = (event) => {
    event.preventDefault();
    const parent = event.target.closest(".nav-item");
    let node = parent.parentNode.firstChild;

    while (node) {
      if (node !== parent && node.nodeType === Node.ELEMENT_NODE) {
        node.classList.remove("show");
      }
      node = node.nextElementSibling || node.nextSibling;
    }

    parent.classList.toggle("show");
    onUpdateSize();
  };

  const renderMenu = (menu, useHashAwareLinks = false) => {
    const items = menu.map((item, key) => {
      let submenu;

      if (item.submenu) {
        submenu = item.submenu.map((subItem, subKey) => {
          const isHashActive =
            useHashAwareLinks &&
            location.hash &&
            subItem.link.endsWith(location.hash);

          if (useHashAwareLinks) {
            return (
              <Link
                to={subItem.link}
                key={subKey}
                className={`nav-sub-link ${isHashActive ? "active" : ""}`}
              >
                {subItem.label}
              </Link>
            );
          }

          return (
            <NavLink to={subItem.link} className="nav-sub-link" key={subKey}>
              {subItem.label}
            </NavLink>
          );
        });
      }

      const isHashActive =
        useHashAwareLinks &&
        location.hash &&
        typeof item.link === "string" &&
        item.link.endsWith(location.hash);

      return (
        <li key={key} className="nav-item">
          {!submenu ? (
            useHashAwareLinks ? (
              <Link
                to={item.link}
                className={`nav-link d-flex align-items-center ${isHashActive ? "active" : ""}`}
              >
                <i className={item.icon}></i>
                <span>{item.label}</span>
              </Link>
            ) : (
              <NavLink to={item.link} className="nav-link">
                <i className={item.icon}></i>
                <span>{item.label}</span>
              </NavLink>
            )
          ) : (
            <div onClick={toggleSubMenu} className="nav-link has-sub">
              <i className={item.icon}></i>
              <span>{item.label}</span>
            </div>
          )}

          {item.submenu && <nav className="nav nav-sub">{submenu}</nav>}
        </li>
      );
    });

    return <ul className="nav nav-sidebar">{items}</ul>;
  };

  return (
    <>
      <div className="mb-3">
        <ChatGlobal />
      </div>

      {menuSections.map((section) => (
        <div className="nav-group show" key={section.label}>
          <div className="nav-label" onClick={toggleMenu}>
            {section.label}
          </div>
          {renderMenu(section.menu, section.useHashAwareLinks)}
        </div>
      ))}

      <div className="sidebar-support-card">
        <span>Need Help</span>
        <h6>Talk to care support</h6>
        <p>Reach support quickly or book your next visit without digging through menus.</p>
        <div className="d-flex gap-2">
          <Link to="/dashboard/support" className="btn btn-light flex-fill">
            Support
          </Link>
          <Link to="/appointment/schedule" className="btn btn-light flex-fill">
            Book
          </Link>
        </div>
      </div>
    </>
  );
};

window.addEventListener("click", function (event) {
  const target = event.target;
  const sidebar = document.querySelector(".sidebar");

  if (!target.closest(".sidebar-footer") && sidebar) {
    sidebar.classList.remove("footer-menu-show");
  }

  if (!target.closest(".sidebar") && !target.closest(".menu-link")) {
    document.querySelector("body").classList.remove("sidebar-show");
  }
});

window.addEventListener("load", function () {
  const skinMode = localStorage.getItem("sidebar-skin");
  const htmlTag = document.querySelector("html");

  if (skinMode) {
    htmlTag.setAttribute("data-sidebar", skinMode);
  }
});

export default Sidebar;
